import { FieldArray, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, TextField, Autocomplete, Grid, FormLabel, InputAdornment, Typography, Box } from '@mui/material';
import GlobalDialogContent from '../GlobalDialogContent';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import { ProjectAssignAgency, StoreAssignAgency } from '../../../utils/services/project/project';
import { RootState } from '../../../redux/store';
import { Add } from '@mui/icons-material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { useEffect, useState } from 'react';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { closeDialog } from '../../../redux/reducer/dialogSlice';
import { setModuleRefresh } from '../../../redux/reducer/registration/RefreshSlice';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { AmountDisplay, rupeeAmountDisplay } from '../../../utils/utils';
import PageLang from '../../../config/page.languge';

const AssignAgency = () => {
    const dispatch = useDispatch();
    const projectData = useSelector((state: RootState) => state.dialog.payload);
    const [agencyOptionsList, setAgencyOptionsList] = useState<any[]>([]);
    const lang = useSelector((state: RootState) => state.lang.language)

    const formik = useFormik({
        initialValues: {
            unitsCommitted: projectData?.no_of_unit || '',
            costPerUnit: projectData?.cost || '',
            project_name: projectData?.project_title || '',
            totalCommitment: projectData?.total_implementation || '',
            formItems: [{ agency_user_id: '', allocated_amount: '', comment: '' }],
        },
        validationSchema: Yup.object({
            unitsCommitted: Yup.number().min(1, PageLang?.dialogues.assignAgencyDialogue[0][lang]).required(PageLang?.dialogues.assignAgencyDialogue[2][lang]),
            costPerUnit: Yup.number().min(0, PageLang?.dialogues.assignAgencyDialogue[1][lang]).required(PageLang?.dialogues.assignAgencyDialogue[2][lang]),
            totalCommitment: Yup.string(),
            formItems: Yup.array().of(
                Yup.object({
                    allocated_amount: Yup.number().required(PageLang?.dialogues.assignAgencyDialogue[3][lang]),
                    comment: Yup.string().required(PageLang?.dialogues.assignAgencyDialogue[4][lang]),
                })
            ),
        }),
        onSubmit: async (values) => {
            if (projectData?.id) {
                const reqPayload = {
                    commitment_id: projectData.id,
                    project_id: projectData.project_id,
                    agency_allocations: values.formItems.map(item => ({
                        remark: item.comment,
                        agency_user_id: item.agency_user_id || 0,
                        allocated_amount: Number(item.allocated_amount) || '',
                    })),
                };
                const { code, message } = await StoreAssignAgency(reqPayload);

                if (code === 200) {
                    dispatch(closeDialog());
                    dispatch(setModuleRefresh({ moduleName: 'AssignAgency', refresh: true }));
                    dispatch(showSnackbar({ type: "success", message: message || "Status updated successfully!" }));
                } else {
                    dispatch(showSnackbar({ type: "error", message: message || "Failed to update status" }));
                }
            }
        },
    });

    useEffect(() => {
        fetchImpAgency();
    }, []);
    
    const fetchImpAgency = async () => {
        const reqPayload = { search: '' };
        const { code, data, message } = await ProjectAssignAgency(reqPayload);
    
        if (code === 200) {
            setAgencyOptionsList(data);
        } else {
            dispatch(showSnackbar({ type: "error", message: message || "Something went wrong!" }));
            setAgencyOptionsList([]);
        }
    };
    

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <GlobalDialogContent
                    dialogBody={
                        <>
                            <Grid container spacing={1} >
                                <Grid item xs={12} sm={12}>
                                    <TextField
                                        label={PageLang?.dialogues.assignAgencyDialogue[5][lang]}
                                        value={formik.values.project_name}
                                        size="small"
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label={PageLang?.dialogues.assignAgencyDialogue[6][lang]}
                                        name="unitsCommitted"
                                        size="small"
                                        value={formik.values.unitsCommitted}
                                        InputProps={{ disabled: true }}
                                        inputProps={{ maxLength: 15, style: { textAlign: 'right' } }}

                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label={PageLang?.dialogues.assignAgencyDialogue[7][lang]}
                                        name="costPerUnit"
                                        size="small"
                                        value={rupeeAmountDisplay(formik.values.costPerUnit)}
                                        InputProps={{ disabled: true }}
                                        inputProps={{ maxLength: 15, style: { textAlign: 'right' } }}

                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label={PageLang?.dialogues.assignAgencyDialogue[8][lang]}
                                        name="totalCommitment"
                                        size="small"
                                        InputProps={{ disabled: true }}
                                        value={rupeeAmountDisplay(formik.values.totalCommitment)}
                                        inputProps={{ maxLength: 15, style: { textAlign: 'right' } }}

                                    />
                                </Grid>
                            </Grid>
                            <FieldArray name="formItems">
                                {({ push, remove }) => (
                                    <Box>
                                        {formik.values.formItems.map((item: any, index: any) => (
                                            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }} key={index}>
                                                <Grid item xs={12} sm={12} md={12}>
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        sx={{ backgroundColor: "#ffcccc", borderRadius: 1 }}
                                                    >
                                                        <Typography variant='h6' sx={{ p: 1 }}>{index + 1}. {PageLang?.dialogues.assignAgencyDialogue[9][lang]} </Typography>
                                                        <Button color="primary" disabled={formik.values.formItems.length === 1} onClick={() => remove(index)}>
                                                            <RemoveCircleOutlineIcon />
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={5} md={5} sx={{ mt: 1 }}>
                                                    <Autocomplete
                                                        options={agencyOptionsList}
                                                        getOptionLabel={(option) => option?.name || ""}
                                                        value={
                                                            agencyOptionsList.find(
                                                                (option: any) => option.id === item.agency_user_id
                                                            ) || null
                                                        }
                                                        onChange={(e, value) => {
                                                            formik.setFieldValue(
                                                                `formItems.${index}.agency_user_id`,
                                                                value?.id || ''
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                label={PageLang?.dialogues.assignAgencyDialogue[11][lang]}
                                                            />
                                                        )}
                                                    />

                                                </Grid>
                                                <Grid item xs={12} sm={3} md={3} sx={{ mt: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        label={PageLang?.dialogues.assignAgencyDialogue[12][lang]}
                                                        name={`formItems.${index}.allocated_amount`}
                                                        size="small"
                                                        value={AmountDisplay(item.allocated_amount || 0)}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/[^0-9]/g, "");
                                                            formik.setFieldValue(`formItems.${index}.allocated_amount`, value);
                                                        }}
                                                        inputProps={{ maxLength: 15, style: { textAlign: 'right' } }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <CurrencyRupeeIcon sx={{ fontSize: 20, ml: 2 }} />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={12} md={12}>
                                                    <FormLabel>{PageLang?.projectCreationFormData.status[3][lang]} <span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <TextField
                                                        name={`formItems.${index}.comment`}
                                                        value={item.comment || ""}
                                                        onChange={(e) =>
                                                            formik.setFieldValue(`formItems.${index}.comment`, e.target.value)
                                                        }
                                                        placeholder={PageLang?.dialogues.assignAgencyDialogue[13][lang]}
                                                        multiline
                                                        rows={3}
                                                        size="small"
                                                        fullWidth
                                                    />
                                                </Grid>
                                            </Grid>
                                        ))}

                                        <Grid item xs={12} container  >
                                            <Typography variant="h6"></Typography>
                                            <Button
                                                startIcon={<Add />}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => push({ agency_user_id: "", allocated_amount: "", comment: "" })
                                                }
                                            >
                                                {PageLang?.dialogues.assignAgencyDialogue[14][lang]}
                                            </Button>
                                        </Grid>
                                    </Box>
                                )}
                            </FieldArray>
                        </>
                    }
                    dialogFooter={
                        <Button type="submit" startIcon={<AssignmentIndIcon />} variant="contained" color="info">
                            {PageLang?.dialogues.assignAgencyDialogue[15][lang]}
                        </Button>
                    }
                />
            </form>
        </FormikProvider>
    );
};

export default AssignAgency;
